import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { INK, LINE, MUTED, SLATE, TEAL, TEAL_HOVER } from "./styles";
import { usePD7A } from "./usePD7A";
import PaymentsTable from "./components/PaymentsTable";
import FilingsTable from "./components/FilingsTable";
import type { TabType } from "./types";

export default function PayrollTaxPrintView() {
  const [searchParams] = useSearchParams();
  const tab: TabType = (searchParams.get("tab") as TabType) || "payments";

  const {
    data: pd7a,
    isLoading: pd7aLoading,
    error: pd7aError,
  } = usePD7A(tab === "payments");

  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    if (pd7a?.company?.name) {
      setCompanyName(pd7a.company.name);
    }
  }, [pd7a]);

  const loading = tab === "payments" ? pd7aLoading : false;
  const errMsg = pd7aError instanceof Error ? pd7aError.message : null;
  const error = errMsg ? "Could not load data: " + errMsg : "";

  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => window.print(), 400);
    return () => clearTimeout(t);
  }, [loading, tab]);

  const title =
    tab === "filings"
      ? "Novala Payroll: Tax filings"
      : "Novala Payroll: Tax payments";

  return (
    <div
      style={{
        margin: 0,
        background: "#EDEFF2",
        fontFamily: "Inter, sans-serif",
        fontSize: 14,
        color: INK,
        minHeight: "100vh",
      }}
    >
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          body { background: #fff !important; }
          .print-bar { display: none !important; }
          .print-sheet {
            box-shadow: none !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: none !important;
            min-height: 100vh !important;
            padding: 0.6in !important;
          }
        }
      `}</style>

      <div
        className="print-bar"
        style={{
          background: "#fff",
          borderBottom: "1px solid #E3E7EC",
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
        }}
      >
        <button
          onClick={() => window.print()}
          style={{
            fontFamily: "inherit",
            fontWeight: 700,
            fontSize: 14,
            border: "none",
            borderRadius: 10,
            padding: "9px 18px",
            cursor: "pointer",
            background: TEAL,
            color: "#fff",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = TEAL_HOVER)}
          onMouseLeave={(e) => (e.currentTarget.style.background = TEAL)}
        >
          Print
        </button>
        <button
          onClick={() => window.close()}
          style={{
            fontFamily: "inherit",
            fontWeight: 700,
            fontSize: 14,
            borderRadius: 10,
            padding: "9px 18px",
            cursor: "pointer",
            background: "#fff",
            color: SLATE,
            border: "1px solid " + LINE,
          }}
        >
          Cancel
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ color: MUTED, fontSize: 13, fontWeight: 600 }}>
          This view opens the browser print dialog. Choose your printer or Save as PDF.
        </div>
      </div>

      <div
        className="print-sheet"
        style={{
          background: "#fff",
          width: 816,
          maxWidth: "100%",
          minHeight: 1056,
          margin: "24px auto",
          padding: "64px 72px",
          boxShadow: "0 1px 6px rgba(16,30,40,0.16)",
        }}
      >
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: SLATE,
            margin: "0 0 6px",
          }}
        >
          {title}
        </h1>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: SLATE,
            margin: "0 0 28px",
          }}
        >
          {companyName || "Company"}
        </div>

        {loading ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: MUTED,
              fontWeight: 600,
            }}
          >
            Loading...
          </div>
        ) : error ? (
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              padding: "10px 12px",
              color: "#991B1B",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        ) : tab === "payments" ? (
          <PaymentsTable pd7a={pd7a} />
        ) : (
          <FilingsTable />
        )}
      </div>
    </div>
  );
}